<script>
import { useFormValidation } from '../../composables/useFormValidation'
import EcErrorBanner from '../ui/EcErrorBanner.vue'
import EcCheckbox from '../ui/EcCheckbox.vue'
import EcInput from '../ui/EcInput.vue'
import EcSelect from '../ui/EcSelect.vue'
import EcTagInput from '../ui/EcTagInput.vue'

export default {
  name: 'ModuleAdditionalConfigForm',
  components: { EcErrorBanner, EcCheckbox, EcInput, EcSelect, EcTagInput },

  model: {
    prop: 'modelValue',
    event: 'update:modelValue',
  },

  props: {
    modelValue: { type: Object, default: () => ({}) },
    fields: { type: Array, default: () => [] },
    disabled: { type: Boolean, default: false },
    title: { type: String, default: '' },
    formErrors: { type: Object, default: null },
  },
  data() {
    const { apiGlobalErrors, apiRawError, setApiValidation, resetApiValidation } = useFormValidation()
    return {
      apiGlobalErrors,
      apiRawError,
      setApiValidation,
      resetApiValidation,
      config: this.modelValue || {},
    }
  },
  computed: {
    isDisabled() {
      return this.disabled ?? false
    },
  },
  watch: {
    modelValue: {
      handler(newVal) {
        this.config = newVal ?? {}
        this.applyDefaultValues()
      },
      deep: true,
    },
    config: {
      handler(newVal) {
        this.$emit('update:modelValue', newVal)
      },
      deep: true,
    },
    formErrors(errors) {
      if (errors) this.setApiValidation(errors)
      else this.resetApiValidation()
    },
  },
  mounted() {
    this.applyDefaultValues()
  },
  methods: {
    applyDefaultValues() {
      if (!this.fields) return
      this.fields.forEach((field) => {
        if (field.default !== undefined && this.config[field.key] === undefined) {
          this.$set(this.config, field.key, field.default)
        }
        if (field.type === 'select' && Array.isArray(field.values)) {
          const validValues = field.values.map((v) => v.value)
          if (!validValues.includes(this.config[field.key])) {
            this.$set(this.config, field.key, null)
          }
        }
      })
    },
    getSelectOptions(field) {
      if (!field.values) return []
      return field.values.map((item) => ({
        value: item.value ?? item,
        label: item.label ?? String(item.value ?? item),
      }))
    },
  },
}
</script>

<template>
  <div class="ec-config-form">
    <EcErrorBanner :error="apiRawError ?? apiGlobalErrors" class="ec-mb-3" />

    <div v-for="field in fields" :key="field.key" class="ec-config-form__field">
      <!-- Boolean: full clickable block with description -->
      <EcCheckbox
        v-if="field.type === 'boolean'"
        v-model="config[field.key]"
        :label="field.label"
        :description="field.description"
        :disabled="isDisabled"
      />

      <!-- Select -->
      <EcSelect
        v-else-if="field.type === 'select'"
        v-model="config[field.key]"
        :label="field.label"
        :options="getSelectOptions(field)"
        :placeholder="field.label"
        :disabled="isDisabled"
        :hint="field.description"
      />

      <!-- Multi-select with tag creation (emails, etc.) -->
      <EcTagInput
        v-else-if="field.type === 'multi_select_allow_create'"
        v-model="config[field.key]"
        :label="field.label"
        :placeholder="field.label"
        :disabled="isDisabled"
        :hint="field.description"
      />

      <!-- Textarea -->
      <div v-else-if="field.type === 'textarea'" class="ec-form-group">
        <label class="ec-form-label">{{ field.label }}</label>
        <textarea
          v-model="config[field.key]"
          class="ec-textarea"
          :disabled="isDisabled"
          :placeholder="field.label"
          rows="4"
        />
        <small v-if="field.description" class="ec-form-hint">{{ field.description }}</small>
      </div>

      <!-- Standard input -->
      <template v-else>
        <EcInput
          v-model="config[field.key]"
          :type="field.type"
          :label="field.label"
          :disabled="isDisabled"
          :placeholder="field.label"
          :hint="field.description"
        />
      </template>
    </div>
  </div>
</template>
